<template>
  <div :class="embedded ? '' : 'row justify-content-center'" role="region" aria-live="polite">
    <div :class="embedded ? '' : 'col-lg-6'">
      <component :is="embedded ? 'div' : 'card'" :style="embedded ? {} : { 'background-color': theme.card_color }">
        <!-- State: asking -->
        <div v-if="state === 'asking'" class="text-center px-3 py-3">
          <h4 :style="{ 'font-family': theme.font_family_card, 'color': theme.text_color_card }" class="mb-1">
            {{ $t('returningCustomer.haveYouPlayedHereBefore') }}
          </h4>
          <p :style="{ 'font-family': theme.font_family_card, 'color': theme.text_color_card }" class="mb-4 small">
            {{ $t('returningCustomer.skipFillingHint') }}
          </p>
          <div class="row justify-content-center">
            <div class="col-lg-6 mb-3">
              <base-button
                :style="{ 'font-family': theme.font_family_card, 'border-color': theme.button_color, 'color': theme.button_color, 'width': '100%', 'white-space': 'nowrap' }"
                simple
                v-on:click="onYes">
                {{ $t('returningCustomer.yesPlayedBefore') }}
              </base-button>
            </div>
            <div class="col-lg-6 mb-3">
              <base-button
                :style="{ 'font-family': theme.font_family_card, 'border-color': theme.button_color, 'color': theme.button_color, 'width': '100%', 'white-space': 'nowrap' }"
                simple
                v-on:click="onNew">
                {{ $t('returningCustomer.noImNew') }}
              </base-button>
            </div>
          </div>
        </div>

        <!-- State: email -->
        <div v-if="state === 'email' || state === 'checking'" class="text-center px-3 py-3">
          <h4 :style="{ 'font-family': theme.font_family_card, 'color': theme.text_color_card }" class="mb-1">
            {{ $t('returningCustomer.welcomeBack') }}
          </h4>
          <p :style="{ 'font-family': theme.font_family_card, 'color': theme.text_color_card }" class="mb-4 small">
            {{ $t('returningCustomer.enterEmailHint') }}
          </p>
          <div class="text-left">
            <label for="rcg-email" :style="{ 'color': theme.text_color_card, 'font-family': theme.font_family_card }">
              {{ $t('checkin.email') }}
            </label>
            <base-input
              id="rcg-email"
              type="email"
              autocomplete="email"
              inputmode="email"
              :error="emailError || (emailBlurred ? emailTypoWarning(email) : '')"
              :secondary-color="theme.text_color_card"
              :input-style="{ 'color': theme.text_color_card }"
              :placeholder="$t('returningCustomer.emailPlaceholder')"
              v-model="email"
              @keyup.enter="onCheck"
              @blur="emailBlurred = true"></base-input>
          </div>
          <div class="row mt-3 mb-2 justify-content-center align-items-center">
            <div class="col-lg-1 col-2">
              <base-button
                :style="{ 'font-family': theme.font_family_card, 'border-color': theme.button_color, 'color': theme.button_color, 'width': '100%', 'font-size': 'large', 'font-weight': 'bold' }"
                v-on:click="onBack" link
                :disabled="state === 'checking'"
                :aria-label="$t('returningCustomer.back')">
                <i :style="{ 'font-size': 'x-large', 'font-weight': 'bold' }" class="tim-icons el-icon-back"></i>
              </base-button>
            </div>
            <div class="col">
              <base-button
                :style="{ 'font-family': theme.font_family_card, 'border-color': theme.button_color, 'color': theme.button_color, 'min-width': '8rem' }"
                simple class="game-button"
                v-on:click="onCheck"
                :disabled="state === 'checking'">
                <span v-if="state === 'checking'">{{ $t('returningCustomer.checking') }}</span>
                <span v-else>{{ $t('returningCustomer.continue') }}</span>
              </base-button>
            </div>
          </div>
        </div>

        <!-- State: recognized -->
        <div v-if="state === 'recognized'" class="text-center px-3 py-3">
          <h4 :style="{ 'font-family': theme.font_family_card, 'color': theme.text_color_card }" class="mb-1">
            {{ $t('returningCustomer.recognized') }}
          </h4>
          <p :style="{ 'font-family': theme.font_family_card, 'color': theme.text_color_card }" class="mb-4 small">
            {{ $t('returningCustomer.reuseHint') }}
          </p>
          <fieldset class="modify-fieldset text-left">
            <legend class="sr-only">{{ $t('returningCustomer.modifyFieldsLegend') }}</legend>
            <div v-for="field in modifiableFields" :key="field.key" class="modify-row">
              <base-checkbox
                :border-color="theme.button_color"
                :accent-color="theme.button_color"
                :check-mark-color="theme.card_color"
                v-model="modify[field.key]"
                @input="onModifyToggle(field.key)"
                simple>
                <span :style="{ 'font-family': theme.font_family_card, 'color': theme.text_color_card }">
                  {{ $t(field.labelKey) }}
                </span>
              </base-checkbox>
              <div v-if="modify[field.key]" class="modify-input-row mt-2 mb-2">
                <div v-if="field.key === 'name'" class="row">
                  <div class="col-md-6">
                    <label :for="'rcg-first-name'" :style="{ 'color': theme.text_color_card, 'font-family': theme.font_family_card }">
                      {{ $t('checkin.firstName') }}
                    </label>
                    <base-input
                      id="rcg-first-name"
                      ref="nameInput"
                      autocomplete="given-name"
                      :secondary-color="theme.text_color_card"
                      :input-style="{ 'color': theme.text_color_card }"
                      :placeholder="$t('checkin.firstName')"
                      v-model="newValues.first_name"></base-input>
                  </div>
                  <div class="col-md-6">
                    <label :for="'rcg-last-name'" :style="{ 'color': theme.text_color_card, 'font-family': theme.font_family_card }">
                      {{ $t('checkin.lastName') }}
                    </label>
                    <base-input
                      id="rcg-last-name"
                      autocomplete="family-name"
                      :secondary-color="theme.text_color_card"
                      :input-style="{ 'color': theme.text_color_card }"
                      :placeholder="$t('checkin.lastName')"
                      v-model="newValues.last_name"></base-input>
                  </div>
                </div>
                <div v-else-if="field.key === 'phone'">
                  <label for="rcg-phone" :style="{ 'color': theme.text_color_card, 'font-family': theme.font_family_card }">
                    {{ $t('checkin.phone') }}
                  </label>
                  <base-input
                    id="rcg-phone"
                    ref="phoneInput"
                    :secondary-color="theme.text_color_card"
                    :input-style="{ 'color': theme.text_color_card }">
                    <phone-number-input
                      :input-style="{ 'color': theme.text_color_card }"
                      v-model="newValues.phone"
                      @phone-update="onPhoneUpdate"
                      :default-country="defaultCountry || 'US'" />
                  </base-input>
                </div>
                <div v-else-if="field.key === 'birthday'">
                  <label for="rcg-birthday" :style="{ 'color': theme.text_color_card, 'font-family': theme.font_family_card }">
                    {{ $t('checkin.birthday') }}
                  </label>
                  <birthday-input
                    input-id="rcg-birthday"
                    ref="birthdayInput"
                    :mode="birthdayMode"
                    :text-color="theme.text_color_card"
                    :card-color="theme.card_color"
                    :date-mask="dateMask"
                    :date-placeholder="datePlaceholder"
                    :date-format="dateFormat"
                    :first-day-of-week="firstDayOfWeek"
                    v-model="newValues.birthday"></birthday-input>
                </div>
              </div>
            </div>
          </fieldset>
          <div class="row mt-3 mb-2 justify-content-center align-items-center">
            <div class="col-lg-1 col-2">
              <base-button
                :style="{ 'font-family': theme.font_family_card, 'border-color': theme.button_color, 'color': theme.button_color, 'width': '100%', 'font-size': 'large', 'font-weight': 'bold' }"
                v-on:click="onBackToEmail" link
                :aria-label="$t('returningCustomer.back')">
                <i :style="{ 'font-size': 'x-large', 'font-weight': 'bold' }" class="tim-icons el-icon-back"></i>
              </base-button>
            </div>
            <div class="col">
              <base-button
                :style="{ 'font-family': theme.font_family_card, 'border-color': theme.button_color, 'color': theme.button_color }"
                simple class="game-button"
                v-on:click="onConfirm">
                {{ $t('returningCustomer.confirmAndContinue') }}
              </base-button>
            </div>
          </div>
        </div>

        <!-- State: not-found -->
        <div v-if="state === 'not-found'" class="text-center px-3 py-3">
          <h4 :style="{ 'font-family': theme.font_family_card, 'color': theme.text_color_card }" class="mb-1">
            {{ $t('returningCustomer.notFoundTitle') }}
          </h4>
          <p :style="{ 'font-family': theme.font_family_card, 'color': theme.text_color_card }" class="mb-4 small">
            {{ $t('returningCustomer.notFoundHint') }}
          </p>
          <div class="row justify-content-center">
            <div class="col-lg-6 mb-3">
              <base-button
                :style="{ 'font-family': theme.font_family_card, 'border-color': theme.button_color, 'color': theme.button_color, 'width': '100%', 'white-space': 'nowrap' }"
                simple
                v-on:click="onTryAgain">
                {{ $t('returningCustomer.tryAgain') }}
              </base-button>
            </div>
            <div class="col-lg-6 mb-3">
              <base-button
                :style="{ 'font-family': theme.font_family_card, 'border-color': theme.button_color, 'color': theme.button_color, 'width': '100%', 'white-space': 'nowrap' }"
                simple
                v-on:click="onContinueAsNew">
                {{ $t('returningCustomer.continueAsNew') }}
              </base-button>
            </div>
          </div>
        </div>
      </component>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Card, BaseButton, BaseInput, BaseCheckbox } from 'src/components'
import PhoneNumberInput from 'src/components/PhoneNumberInput.vue'
import BirthdayInput from 'src/components/BirthdayInput.vue'
import emailTypoWarning from 'src/mixins/emailTypoWarning'

export default {
  name: 'returning-customer-gate',
  components: { Card, BaseButton, BaseInput, BaseCheckbox, PhoneNumberInput, BirthdayInput },
  mixins: [emailTypoWarning],
  props: {
    apiLocation: { type: String, required: true },
    code: { type: String, required: true },
    waiverType: { type: String, default: 'traditional' },
    theme: { type: Object, required: true },
    dateMask: { type: [String, Array], default: '##/##/####' },
    datePlaceholder: { type: String, default: 'MM/DD/YYYY' },
    defaultCountry: { type: String, default: 'US' },
    embedded: { type: Boolean, default: false },
    birthdayMode: { type: String, default: 'typed' },
    dateFormat: { type: String, default: '' },
    firstDayOfWeek: { type: Number, default: 1 },
  },
  data() {
    return {
      state: 'asking',
      email: '',
      emailError: '',
      emailBlurred: false,
      modify: { name: false, phone: false, birthday: false },
      newValues: { first_name: '', last_name: '', phone: '', phoneE164: '', birthday: '' },
      modifiableFields: [
        { key: 'name', labelKey: 'returningCustomer.changeName' },
        { key: 'phone', labelKey: 'returningCustomer.changePhone' },
        { key: 'birthday', labelKey: 'returningCustomer.changeBirthday' },
      ],
    }
  },
  methods: {
    resetModifyFields() {
      this.modify = { name: false, phone: false, birthday: false }
      this.newValues = { first_name: '', last_name: '', phone: '', phoneE164: '', birthday: '' }
    },
    onPhoneUpdate(phoneData) {
      this.newValues.phoneE164 = phoneData && phoneData.e164 ? phoneData.e164 : ''
    },
    onYes() {
      this.state = 'email'
      this.emailError = ''
    },
    onNew() {
      this.$emit('declined')
    },
    onBack() {
      this.state = 'asking'
      this.email = ''
      this.emailError = ''
      this.resetModifyFields()
    },
    onTryAgain() {
      this.state = 'email'
      this.email = ''
      this.emailError = ''
      this.resetModifyFields()
    },
    onBackToEmail() {
      this.state = 'email'
      this.resetModifyFields()
    },
    onContinueAsNew() {
      this.$emit('declined')
    },
    isValidEmail(value) {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return re.test(value)
    },
    async onCheck() {
      const trimmed = (this.email || '').trim()
      if (!this.isValidEmail(trimmed)) {
        this.emailError = this.$t('returningCustomer.invalidEmail')
        return
      }
      this.state = 'checking'
      this.emailError = ''
      try {
        const url = this.apiLocation + 'bookings/company-groups/' + this.code + '/returning-customer-check'
        const resp = await axios.post(url, { email: trimmed, type: this.waiverType })
        if (this.state !== 'checking') return
        if (resp.data && resp.data.recognized === true) {
          this.email = trimmed
          this.state = 'recognized'
        } else {
          this.state = 'not-found'
        }
      } catch (e) {
        if (this.state !== 'checking') return
        this.state = 'not-found'
      }
    },
    onModifyToggle(key) {
      if (!this.modify[key]) return
      this.$nextTick(() => {
        const refMap = { name: 'nameInput', phone: 'phoneInput', birthday: 'birthdayInput' }
        const ref = this.$refs[refMap[key]]
        const el = ref && ref.$el ? ref.$el.querySelector('input') : null
        if (el) el.focus()
      })
    },
    onConfirm() {
      const partialUser = {}
      if (this.modify.name) {
        if (this.newValues.first_name) partialUser.first_name = this.newValues.first_name
        if (this.newValues.last_name) partialUser.last_name = this.newValues.last_name
      }
      if (this.modify.phone) {
        const phone = this.newValues.phoneE164 || this.newValues.phone
        if (phone) partialUser.phone = phone
      }
      if (this.modify.birthday && this.newValues.birthday) partialUser.birthday = this.newValues.birthday
      this.$emit('confirmed', { email: this.email, partialUser })
    },
  },
}
</script>

<style lang="scss" scoped>
.modify-fieldset {
  border: 0;
  padding: 0;
  margin: 0 0 0.5rem 0;
}
.modify-row {
  margin-bottom: 0.75rem;
}
.modify-input-row {
  margin-left: 1.75rem;
  transition: opacity 150ms ease-out;
}
</style>
